/** @jsxImportSource @emotion/react */
import { useMutation } from "@apollo/client";
import { css } from "@emotion/react";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MarketplaceGamDataContext } from "../../../context/marketplaceGamDataContext";
import { MarketplaceGlobalDataContext } from "../../../context/marketplaceGlobalDataContext";
import { refreshMarketplaceOrder } from "../../../reducers/marketplaceOrdersReducer";
import { pushMessage } from "../../../reducers/messagingReducer";
import {
    ADGRID_GAM_NETWORK_CODE,
    LINE_ITEM_STATS_MUTATION,
    LINEITEM_ACTIONS,
    LINEITEM_STATUS,
    MARKETPLACE_ACTION_MUTATION,
    MARKETPLACE_DOMAIN_STATUS,
    MARKETPLACE_ORDER_SSP_UPDATE,
    MARKETPLACE_ORDERS_REVIEW_MUTATION
} from "./constants";
import Cta, { PauseCta, PlayCta } from "./CTA";
import { requestLineItemsStats } from "./functions";

/**
 * 
 * @param {Object} param0
 * @param {MarketplaceOrder} param0.order
 * @param {number} param0.idx
 * @param {string} param0.currentCustomer
 * @returns {React.JSX.Element}
 */

export default function MarketplaceRowControls({
    deleteTemporaryProperties,
    deleteOrderHandler,
    updateOrderStats,
    order,
    idx,
    currentCustomer,
}) {
    const dispatch = useDispatch();
    const { gamStats } = useContext(MarketplaceGamDataContext)


    /** @type {{domainToNetworkMappings: Object.<string, DomainInformation>, allOrders: MarketplaceOrder[]}} */
    const globalData = useContext(MarketplaceGlobalDataContext)
    const { domainToNetworkMappings, allOrders } = globalData

    const [marketplaceActionMutation] = useMutation(MARKETPLACE_ACTION_MUTATION);
    const [marketplaceOrderReviewMutation] = useMutation(MARKETPLACE_ORDERS_REVIEW_MUTATION);
    const [lineItemStatsMutation] = useMutation(LINE_ITEM_STATS_MUTATION);
    const [marketplaceOrderSspUpdate] = useMutation(MARKETPLACE_ORDER_SSP_UPDATE)

    const [showPauseControl, setShowPauseControl] = useState(false);
    const [showResumeControl, setShowResumeControl] = useState(false);
    const { lineItemId, wpMarketplacePostId, originalCustomer } = order
    const orderStatus = gamStats?.[lineItemId]?.status || 'Draft'
    const { userID: userId } = useSelector((state) => state.userInformation);


    useEffect(() => {
        if (lineItemId) {
            if (orderStatus === LINEITEM_STATUS.PAUSED) {
                setShowResumeControl(true);
                setShowPauseControl(false);
            }
            if (orderStatus === LINEITEM_STATUS.READY || orderStatus === LINEITEM_STATUS.DELIVERING) {
                setShowResumeControl(false);
                setShowPauseControl(true);
            }
        }
    }, [orderStatus, lineItemId]);

    async function marketplaceOrderActionHandler() {
        try {
            let networks = {};
            let newStatuses = { ...order.networkStatuses };
            let domains = [];
            let adapterDomains = [];

            for (let [domainId, { status }] of Object.entries(order.networkStatuses)) {
                let isRejected = status === MARKETPLACE_DOMAIN_STATUS.REJECTED
                let isPending = status === MARKETPLACE_DOMAIN_STATUS.PENDING

                if (isRejected || isPending || !domainToNetworkMappings[domainId]) {
                    continue;
                }

                let isRunning = status === MARKETPLACE_DOMAIN_STATUS.APPROVED ||
                    status === MARKETPLACE_DOMAIN_STATUS.RESUMED ||
                    status === MARKETPLACE_DOMAIN_STATUS.RESUMED_BY_BUYER ||
                    status === MARKETPLACE_DOMAIN_STATUS.DELIVERING ||
                    status === MARKETPLACE_DOMAIN_STATUS.RESUMED_BY_SELLER;

                /* if this domain has already been paused by the seller or buyer, then keep this domain status unchanged */
                if (status === MARKETPLACE_DOMAIN_STATUS.PAUSED_BY_SELLER) {
                    continue;
                }

                if (status === MARKETPLACE_DOMAIN_STATUS.RESUMED && !showPauseControl) {
                    continue;
                }

                let networkCode = domainToNetworkMappings[domainId]?.networkCode;

                if (!networkCode) {
                    delete order.networkStatuses?.[domainId];
                    continue;
                }

                let lineItemId = order?.approved_orders_mappings?.[domainId]?.lineitem_id
                let isAdapterDomain = domainToNetworkMappings[domainId].isAdapterDomain

                newStatuses[domainId] = {
                    status: isRunning
                        ? MARKETPLACE_DOMAIN_STATUS.PAUSED_BY_BUYER
                        : MARKETPLACE_DOMAIN_STATUS.RESUMED
                };

                if (isAdapterDomain) {
                    adapterDomains.push(domainId)
                } else if (lineItemId) {
                    networks[networkCode] = networks[networkCode] || [];
                    networks[networkCode].push(lineItemId);
                } else {
                    continue
                }

                domains.push(domainId);
            }


            order.networkStatuses = newStatuses;
            order.inProgress = true;
            order.pausing = showPauseControl;
            order.resuming = showResumeControl;

            networks[ADGRID_GAM_NETWORK_CODE] = [lineItemId];

            dispatch(refreshMarketplaceOrder(order, idx, currentCustomer));

            await marketplaceActionMutation({
                variables: {
                    networks: JSON.stringify(networks),
                    action: showPauseControl ? LINEITEM_ACTIONS.PAUSE : LINEITEM_ACTIONS.RESUME,
                },
            });

            if (adapterDomains.length) {
                let domainStatuses = adapterDomains
                    .reduce((acc, curr) => {
                        acc[curr] = showPauseControl ? 'paused' : 'ready'
                        return acc
                    }, {})
                await marketplaceOrderSspUpdate({
                    variables: {
                        data: JSON.stringify({
                            customer_name: originalCustomer || '',
                            domainStatuses
                        }),
                        orderId: String(wpMarketplacePostId),
                        op: showPauseControl ? 'BATCH_PAUSE' : 'BATCH_RESUME',
                        orderStatus: showPauseControl ? 'paused' : 'ready',
                        userId: String(userId)
                    }
                })
            }

            let response = await requestLineItemsStats(
                allOrders.filter(order => order.lineItemId).map(order => order.lineItemId),
                lineItemStatsMutation
            );

            updateOrderStats(response)

            if (domains.length) {
                await marketplaceOrderReviewMutation({
                    variables: {
                        domainIds: JSON.stringify(domains),
                        domainStatus: showPauseControl
                            ? MARKETPLACE_DOMAIN_STATUS.PAUSED_BY_BUYER
                            : MARKETPLACE_DOMAIN_STATUS.RESUMED,
                        marketplace_order_id: String(order.wpMarketplacePostId),
                    },
                });
            }

            deleteTemporaryProperties(order);
            dispatch(pushMessage("Marketplace: ", "Order has been updated"));
        } catch (err) {
            deleteTemporaryProperties(order);
            dispatch(refreshMarketplaceOrder(order, idx, currentCustomer));
            dispatch(pushMessage("Marketplace: ", err.message, "error"));
        }
    }

    return (
        <div
            css={css`
                display: flex;
                align-items: center;
                margin-right: 1em;
                .order-info {
                    margin-right: 1em;
                    .status-code {
                    }
                }
                .status {
                    margin-bottom: 0;
                    font-weight: 400;
                }
                .status-code {
                    text-transform: lowercase;
                }
            `}
        >
            {lineItemId ? (
                <div className="order-info">
                    <span className="status">
                        {orderStatus ? (
                            <>
                                Order status: <span className={`status-code ${orderStatus}`}>{orderStatus}</span>
                            </>
                        ) : (
                            <>
                                Retrieving status <i className="pi pi-spin pi-spinner" style={{ fontSize: "1em" }}></i>
                            </>
                        )}
                    </span>
                </div>
            ) : (
                <div className="order-info">
                    <span className="status">Order status: Draft</span>
                </div>
            )}

            <div>
                <div>
                    {showPauseControl && (
                        <PauseCta
                            ctaText="Pause Order"
                            themeClasses="marketplace-cta outlined warning"
                            onClick={(ev) => {
                                ev.stopPropagation();
                                marketplaceOrderActionHandler();
                            }}
                        />
                    )}
                    {showResumeControl && (
                        <PlayCta
                            ctaText="Resume Order"
                            themeClasses="marketplace-cta outlined success"
                            onClick={(ev) => {
                                ev.stopPropagation();
                                marketplaceOrderActionHandler();
                            }}
                        />
                    )}
                </div>

                {!lineItemId && (
                    <div>
                        <Cta
                            ctaText="Delete order"
                            icon={faCircleXmark}
                            onClick={(ev) => {
                                ev.stopPropagation();
                                deleteOrderHandler();
                            }}
                            themeClasses="marketplace-cta outlined danger"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
